import { Graphics, GraphicsContext } from 'pixi.js';
import { GLYPH_VIEWBOX, type GlyphName, glyphInnerSvg, isGlyphName } from '../glyphs/glyphPaths';
import { TILE_GLYPH_FILL, TILE_GLYPH_STROKE } from './faceColour';

/**
 * Pixi `Graphics` glyph drawer (HEX-102, ADR-0007 keeps Pixi imports under this
 * directory). Renders the shared glyph set ([[glyphPaths]] `GLYPHS`) onto the
 * overworld via Pixi's SVG path parser, replacing the pre-HEX-102 unicode `Text`
 * glyphs.
 *
 * Each glyph is drawn twice into one `GraphicsContext`: a dark, wider HALO pass
 * first, then the light INK pass — so the line art reads against any biome tint
 * the way the old `Text` glyph's dark stroke halo did (functional legibility
 * parity). The context is parsed once per glyph name and shared across every
 * tile that shows it (`new Graphics(context)`); `Graphics.destroy()` defaults to
 * leaving the shared context intact.
 */

/** Ink (foreground) stroke width, viewBox units — matches the handoff `sw`. */
const INK_STROKE_WIDTH = 1.7;
/** Halo (dark underlay) stroke width — wider so a dark rim peeks past the ink. */
const HALO_STROKE_WIDTH = 4.4;

/**
 * Fraction of the hex radius the 24-unit glyph spans across. ~1.0 keeps the
 * glyph comfortably inside a pointy-top hex (height `2·size`) while reading at
 * the same weight as the old `Text` glyph (`fontSize ≈ 0.62·size`).
 */
export const GLYPH_SPAN_FRACTION = 1.0;

function toHex(colour: number): string {
  return `#${(colour & 0xffffff).toString(16).padStart(6, '0')}`;
}

const contextCache = new Map<GlyphName, GraphicsContext>();

/**
 * The shared, parsed `GraphicsContext` for `name` (halo + ink), built on first
 * request and cached. Reused across all tiles painting the same glyph.
 */
export function glyphContext(name: GlyphName): GraphicsContext {
  const cached = contextCache.get(name);
  if (cached) return cached;
  // Defensive (HEX-109): an unmapped name (e.g. a legacy `TileType` whose glyph
  // never existed) yields an empty, paint-nothing context rather than throwing
  // mid-render and aborting the whole tile-paint loop into a black world.
  if (!isGlyphName(name)) {
    const empty = new GraphicsContext();
    contextCache.set(name, empty);
    return empty;
  }
  const halo = toHex(TILE_GLYPH_STROKE);
  const ink = toHex(TILE_GLYPH_FILL);
  const haloPass = glyphInnerSvg(name, {
    stroke: halo,
    fill: halo,
    strokeWidth: HALO_STROKE_WIDTH,
  });
  const inkPass = glyphInnerSvg(name, { stroke: ink, fill: ink, strokeWidth: INK_STROKE_WIDTH });
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${GLYPH_VIEWBOX} ${GLYPH_VIEWBOX}">${haloPass}${inkPass}</svg>`;
  const context = new GraphicsContext().svg(svg);
  contextCache.set(name, context);
  return context;
}

/**
 * Build a centred, hex-scaled `Graphics` for `name`. The node's pivot is the
 * glyph centre, so positioning it at a hex's pixel centre centres the glyph;
 * `hexSize` is the layout radius so the glyph scales with zoom. Eventless — the
 * underlying hex graphic owns hover / click.
 */
export function buildGlyphGraphics(name: GlyphName, hexSize: number): Graphics {
  const glyph = new Graphics(glyphContext(name));
  glyph.pivot.set(GLYPH_VIEWBOX / 2, GLYPH_VIEWBOX / 2);
  glyph.scale.set((hexSize * GLYPH_SPAN_FRACTION) / GLYPH_VIEWBOX);
  glyph.eventMode = 'none';
  return glyph;
}

/** Drop the cached contexts — test seam; production never needs to evict. */
export function clearGlyphContextCache(): void {
  for (const context of contextCache.values()) context.destroy();
  contextCache.clear();
}
